import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, FilterIcon, PlusIcon, DownloadIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface ServiceRequest {
  id: string;
  serviceType: string;
  category: string;
  requestDate: string;
  completionDate: string | null;
  status: 'completed' | 'pending' | 'cancelled' | 'in-progress';
  reference: string;
  fee: string;
  assignedTo: string;
  applicantName: string;
}
const serviceRequests: ServiceRequest[] = [{
  id: '1',
  serviceType: 'New Water Connection',
  category: 'Water Services',
  requestDate: '2024-01-15',
  completionDate: '2024-01-20',
  status: 'completed',
  reference: 'WS-2024-001',
  fee: '$250.00',
  assignedTo: 'John Smith',
  applicantName: 'Yared Tesfaye'
}, {
  id: '2',
  serviceType: 'Water Meter Installation',
  category: 'Water Services',
  requestDate: '2024-01-18',
  completionDate: null,
  status: 'in-progress',
  reference: 'WS-2024-002',
  fee: '$150.00',
  assignedTo: 'Maria Garcia',
  applicantName: 'Bethlehem Alemu'
}, {
  id: '3',
  serviceType: 'Pipe Repair Request',
  category: 'Maintenance & Repairs',
  requestDate: '2024-01-10',
  completionDate: '2024-01-12',
  status: 'completed',
  reference: 'MR-2024-001',
  fee: '$175.00',
  assignedTo: 'David Chen',
  applicantName: 'Kidist Gebremichael'
}, {
  id: '4',
  serviceType: 'Sewage Line Connection',
  category: 'Sanitation Services',
  requestDate: '2024-01-22',
  completionDate: null,
  status: 'pending',
  reference: 'SS-2024-001',
  fee: '$350.00',
  assignedTo: 'Emily Rodriguez',
  applicantName: 'Solomon Desta'
}, {
  id: '5',
  serviceType: 'Water Quality Inspection',
  category: 'Water Services',
  requestDate: '2024-01-17',
  completionDate: '2024-01-19',
  status: 'completed',
  reference: 'WS-2024-003',
  fee: '$125.00',
  assignedTo: 'Thomas Wilson',
  applicantName: 'Hirut Mengiste'
}, {
  id: '6',
  serviceType: 'Drainage System Maintenance',
  category: 'Maintenance & Repairs',
  requestDate: '2024-01-25',
  completionDate: null,
  status: 'in-progress',
  reference: 'MR-2024-002',
  fee: '$200.00',
  assignedTo: 'Sarah Thompson',
  applicantName: 'Tewodros Bekele'
}, {
  id: '7',
  serviceType: 'Emergency Leak Repair',
  category: 'Maintenance & Repairs',
  requestDate: '2024-01-20',
  completionDate: null,
  status: 'in-progress',
  reference: 'MR-2024-003',
  fee: '$275.00',
  assignedTo: 'Michael Lee',
  applicantName: 'Rahel Mekonnen'
}, {
  id: '8',
  serviceType: 'Septic Tank Installation',
  category: 'Sanitation Services',
  requestDate: '2024-01-16',
  completionDate: null,
  status: 'pending',
  reference: 'SS-2024-002',
  fee: '$450.00',
  assignedTo: 'Jennifer Davis',
  applicantName: 'Henok Tekle'
}, {
  id: '9',
  serviceType: 'Water Pressure Check',
  category: 'Water Services',
  requestDate: '2024-01-21',
  completionDate: '2024-01-23',
  status: 'completed',
  reference: 'WS-2024-004',
  fee: '$75.00',
  assignedTo: 'Kevin Brown',
  applicantName: 'Selamawit Yohannes'
}, {
  id: '10',
  serviceType: 'Waste Water Treatment',
  category: 'Sanitation Services',
  requestDate: '2024-01-19',
  completionDate: null,
  status: 'in-progress',
  reference: 'SS-2024-003',
  fee: '$325.00',
  assignedTo: 'Rachel Martinez',
  applicantName: 'Fasil Demeke'
}];
const StatusBadge = ({
  status
}: {
  status: ServiceRequest['status'];
}) => {
  const styles = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  const icons = {
    completed: <CheckCircleIcon className="w-4 h-4" data-id="element-5473" />,
    pending: <ClockIcon className="w-4 h-4" data-id="element-5474" />,
    'in-progress': <AlertCircleIcon className="w-4 h-4" data-id="element-5475" />,
    cancelled: <XCircleIcon className="w-4 h-4" data-id="element-5476" />
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-5477">
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>;
};
export const WaterSanitationPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const handleReferenceClick = (request: ServiceRequest) => {
    navigate('/request-details', {
      state: {
        request
      }
    });
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedCategory('all');
    setDateRange({
      from: '',
      to: ''
    });
    setCurrentPage(1);
  };
  const filteredRequests = serviceRequests.filter(request => {
    const matchesSearch = request.serviceType.toLowerCase().includes(searchQuery.toLowerCase()) || request.reference.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || request.status === selectedStatus;
    const matchesCategory = selectedCategory === 'all' || request.category === selectedCategory;
    // Add date filtering logic if dates are selected
    let matchesDateRange = true;
    if (dateRange.from) {
      matchesDateRange = matchesDateRange && new Date(request.requestDate) >= new Date(dateRange.from);
    }
    if (dateRange.to) {
      matchesDateRange = matchesDateRange && new Date(request.requestDate) <= new Date(dateRange.to);
    }
    return matchesSearch && matchesStatus && matchesCategory && matchesDateRange;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  return <div className="min-h-screen bg-gray-50" data-id="element-5478">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5479">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5480">
          <a href="/" className="hover:text-gray-700" data-id="element-5481">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5482" />
          <span className="text-gray-900" data-id="element-5483">Water & Sanitation Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-5484">
          <div className="flex justify-between items-start" data-id="element-5485">
            <div data-id="element-5486">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5487">
                Service Requests
              </h1>
              <p className="text-gray-600" data-id="element-5488">
                View and manage your water & sanitation service requests
              </p>
            </div>
            <div className="flex gap-3" data-id="element-5489">
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-5490">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-5491" />
                New Service Request
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-5492">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5493" />
                Export
              </button>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6" data-id="element-5494">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5495">
            <div data-id="element-5496">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5497">
                Search
              </label>
              <div className="relative" data-id="element-5498">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5499" />
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search requests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5500" />
              </div>
            </div>
            <div data-id="element-5501">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5502">
                Status
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-5503">
                <option value="all" data-id="element-5504">All Statuses</option>
                <option value="completed" data-id="element-5505">Completed</option>
                <option value="in-progress" data-id="element-5506">In Progress</option>
                <option value="pending" data-id="element-5507">Pending</option>
                <option value="cancelled" data-id="element-5508">Cancelled</option>
              </select>
            </div>
            <div data-id="element-5509">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5510">
                Category
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-5511">
                <option value="all" data-id="element-5512">All Categories</option>
                <option value="Water Services" data-id="element-5513">Water Services</option>
                <option value="Sanitation Services" data-id="element-5514">Sanitation Services</option>
                <option value="Maintenance & Repairs" data-id="element-5515">
                  Maintenance & Repairs
                </option>
              </select>
            </div>
            <div data-id="element-5516">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5517">
                Date Range
              </label>
              <div className="flex flex-col space-y-2" data-id="element-5518">
                <div className="flex gap-2" data-id="element-5519">
                  <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={dateRange.from} onChange={e => setDateRange({
                  ...dateRange,
                  from: e.target.value
                })} data-id="element-5520" />
                  <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={dateRange.to} onChange={e => setDateRange({
                  ...dateRange,
                  to: e.target.value
                })} data-id="element-5521" />
                </div>
                <div className="flex gap-2" data-id="element-5522">
                  <button onClick={() => setCurrentPage(1)} className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-5523">
                    Apply Filter
                  </button>
                  <button onClick={handleClearFilters} className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5524">
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Service Requests Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-5525">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-5526">
            <thead className="bg-gray-50" data-id="element-5527">
              <tr data-id="element-5528">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5529">
                  Reference
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5530">
                  Applicant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5531">
                  Service Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5532">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5533">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5534">
                  Request Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5535">
                  Fee
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-5536">
              {paginatedRequests.map(request => <tr key={request.id} className="hover:bg-gray-50" data-id="element-5537">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-5538">
                    <button onClick={() => handleReferenceClick(request)} className="hover:underline focus:outline-none" data-id="element-5539">
                      {request.reference}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-5540">
                    {request.applicantName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5541">
                    {request.serviceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5542">
                    {request.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5543">
                    <StatusBadge status={request.status} data-id="element-5544" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5545">
                    {new Date(request.requestDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5546">
                    {request.fee}
                  </td>
                </tr>)}
              {/* Add empty rows if needed to maintain consistent height */}
              {paginatedRequests.length < rowsPerPage && Array(rowsPerPage - paginatedRequests.length).fill(0).map((_, index) => <tr key={`empty-${index}`} data-id="element-5547">
                      <td colSpan={7} className="px-6 py-4" data-id="element-5548">
                        &nbsp;
                      </td>
                    </tr>)}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-5549">
            <div className="flex-1 flex justify-between sm:hidden" data-id="element-5550">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5551">
                Previous
              </button>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5552">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-5553">
              <div data-id="element-5554">
                <p className="text-sm text-gray-700" data-id="element-5555">
                  Showing{' '}
                  <span className="font-medium" data-id="element-5556">
                    {filteredRequests.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium" data-id="element-5557">
                    {Math.min(currentPage * rowsPerPage, filteredRequests.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium" data-id="element-5558">{filteredRequests.length}</span>{' '}
                  results
                </p>
              </div>
              <div data-id="element-5559">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-5560">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5561">
                    <span className="sr-only" data-id="element-5562">Previous</span>
                    <ChevronRightIcon className="h-5 w-5 transform rotate-180" data-id="element-5563" />
                  </button>
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-5564">
                      {i + 1}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5565">
                    <span className="sr-only" data-id="element-5566">Next</span>
                    <ChevronRightIcon className="h-5 w-5" data-id="element-5567" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Request Detail Modal */}
      {selectedRequest && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-5568">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-5569">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-5570">
              <div className="flex items-center justify-between" data-id="element-5571">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-5572">
                  Service Request Details
                </h3>
                <button onClick={() => setSelectedRequest(null)} className="text-gray-400 hover:text-gray-500" data-id="element-5573">
                  <XCircleIcon className="h-5 w-5" data-id="element-5574" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4" data-id="element-5575">
              <div className="grid grid-cols-2 gap-4" data-id="element-5576">
                <div data-id="element-5577">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5578">
                    Reference
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5579">
                    {selectedRequest.reference}
                  </p>
                </div>
                <div data-id="element-5580">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5581">
                    Status
                  </label>
                  <p className="mt-1" data-id="element-5582">
                    <StatusBadge status={selectedRequest.status} data-id="element-5583" />
                  </p>
                </div>
                <div data-id="element-5584">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5585">
                    Applicant
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5586">
                    {selectedRequest.applicantName}
                  </p>
                </div>
                <div data-id="element-5587">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5588">
                    Service Type
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5589">
                    {selectedRequest.serviceType}
                  </p>
                </div>
                <div data-id="element-5590">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5591">
                    Category
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5592">
                    {selectedRequest.category}
                  </p>
                </div>
                <div data-id="element-5593">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5594">
                    Fee
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5595">
                    {selectedRequest.fee}
                  </p>
                </div>
                <div data-id="element-5596">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5597">
                    Request Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5598">
                    {new Date(selectedRequest.requestDate).toLocaleDateString()}
                  </p>
                </div>
                <div data-id="element-5599">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5600">
                    Completion Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5601">
                    {selectedRequest.completionDate ? new Date(selectedRequest.completionDate).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div data-id="element-5602">
                  <label className="text-sm font-medium text-gray-500" data-id="element-5603">
                    Assigned To
                  </label>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-5604">
                    {selectedRequest.assignedTo}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-5605">
              <button onClick={() => setSelectedRequest(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5606">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
};